import { h } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import IconCloseSmall from 'utils/icon/icon-close-small.svg';
import IconSearch from 'utils/icon/icon-search.svg';
import IconSupergoop from 'utils/icon/icon-supergoop.svg';
import IconAccount from 'utils/icon/icon-account.svg';
import Panel from 'utils/panel';
import { richTextToHTML } from 'utils/storefront/format-resource';
import MenuList from './menu-list';
import BestSellers from './header-bestsellers';
import HeaderSecondaryNav from './header-secondary-nav';
import YotpoPointBalance from './yotpo-point-balance';

const HeaderNav = ({
  menu,
  linksData,
  bestSellers,
  sunscreenInfo,
  account,
  supergoopText,
  enableHamburgerMenu,
}) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [secondaryMenu, setSecondaryMenu] = useState(null);
  const [isSecondaryNavOpen, setSecondaryNavOpen] = useState(false);
  const promoBar = document.querySelector('[data-section="promo-bar"]');
  const animationType = 'fade'; // or 'slide'
  const isFadeAnimation = animationType === 'fade';


  const sunScreen = useMemo(() => {
    const sunscreenInfoData = richTextToHTML(sunscreenInfo.sunscreen_info);
    return (
      <div className="menu-content ">
        <div
          className="[&_p]:pb-4 [&_strong]:label-12 [&_strong]:tracking-[2px] [&_strong]:uppercase body-xs [&_em]:bg-white [&_em]:rounded [&_em]:px-1 [&_em]:py-0.5 [&_em]:font-semibold [&_em]:not-italic [&_em]:tracking-[2px] mb-[40px] text-grey-2A"
          dangerouslySetInnerHTML={{ __html: sunscreenInfoData }}
        ></div>
      </div>
    );
  }, [sunscreenInfo]);

  const panelTriggers = useMemo(
    () => ({
      openTriggerEl: document.querySelector('[data-nav-trigger]'),
      closeTriggerEl: null,
    }),
    [],
  );

  const handleAddSecondaryMenu = (selectedMenu) => {
    setSecondaryMenu(selectedMenu);
    setSecondaryNavOpen(true);
  };

  useEffect(() => {
    document.querySelector("[data-nav-trigger]").addEventListener('click', () => {
      document.body.style.overflow = 'hidden';
    });

    document.querySelectorAll('[data-nav-item]').forEach((el) => {
      el.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('nav:open'));
        setActiveMenu({ ...menu[parseInt(el.dataset.navItem, 10)] });
        document.body.style.overflow = 'hidden';
      });
    });

    const handleCloseEvent = (e) => {
      if (!e.detail.open) {
        setSecondaryNavOpen(false);
      }
    };

    const handleSecondaryNavCloseEvent = () => {
      setSecondaryNavOpen(false);
      setTimeout(
        () => {
          setSecondaryMenu(null);
        },
        isFadeAnimation ? 500 : 0,
      ); // wait until fade animation ended
    };

    document.addEventListener(`nav:toggle`, handleCloseEvent);
    document.addEventListener(
      `secondary:nav:close`,
      handleSecondaryNavCloseEvent,
    );
    return () => {
      document.removeEventListener(`nav:toggle`, handleCloseEvent);
      document.removeEventListener(
        `secondary:nav:close`,
        handleSecondaryNavCloseEvent,
      );
    };
  }, []);

  return (
    <div>
      <Panel
        triggers={panelTriggers}
        showBackdrop={true}
        context="nav"
        additionalClasses="!max-w-[440px] h-auto"
        offsetTop={promoBar.offsetHeight}
        alignment="left"
        animation={animationType}
      >
        <div className="h-full z-50 border-r overflow-y-auto">
          <div className="bg-white relative">
            <button
              className="absolute pr-5 pt-5 pl-4 lg:hidden"
              type="button"
              tabIndex="0"
              onClick={() => {
                document.dispatchEvent(new CustomEvent('search:open'));
                document.body.style.overflow = 'hidden';
              }}
            >
              <div className="text-black hover:text-supergoop-yellow">
                <IconSearch />
              </div>
            </button>
            <button
              className="absolute top-4 lg:right-4 right-2"
              type="button"
              tabIndex="0"
              onClick={() => {
                document.dispatchEvent(new CustomEvent('nav:close'));
                document.body.style.overflow = '';
              }}
            >
              <div className="text-white hover:text-supergoop-yellow">
                <IconCloseSmall />
              </div>
            </button>
          </div>
          <div className={enableHamburgerMenu ? "mt-0 lg:mt-10 lg:bg-white" : "mt-0 lg:mt-10 lg:bg-white lg:block hidden"}>
            {activeMenu !== null && (
              <MenuList
                {...activeMenu}
                linksData={linksData}
                sunScreen={sunScreen}
                animation={animationType}
                openSubMenu={handleAddSecondaryMenu}
                onBack={() => setActiveMenu(null)}
              />
            )}

            <div
              className={`${activeMenu ? 'left-[100%]' : 'left-[-200%]'
                } absolute bg-white bottom-0 top-0 hidden lg:block`}
            >
              <HeaderSecondaryNav
                isOpen={isSecondaryNavOpen}
                animation={animationType}
                menu={secondaryMenu}
                linksData={linksData}
              />
            </div>

            <ul className="mt-20 px-5 pb-10 block lg:hidden">
              {menu.map((item, index) => (
                <li key={item.id} className="mb-8 last:mb-0">
                  {item.links.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => setActiveMenu(menu[index])}
                      tabIndex="0"
                      className="!sub-h3 cursor-pointer link-list"
                    >
                      {item.title}
                    </button>
                  ) : (
                    <a href={item.url} type='link' className="!sub-h3 cursor-pointer link-list">
                      {item.title}
                    </a>
                  )}
                </li>
              ))}
            </ul>

            <BestSellers {...bestSellers} />

            <ul className="px-5 lg:hidden">
              <li>
                <button
                  className="h-14 bg-grey-F7 rounded flex items-center w-full no-underline mb-2.5"
                  onClick={() => {
                    document.dispatchEvent(new CustomEvent('supergoop:open'))
                    document.body.style.overflow = 'hidden'
                  }
                  }
                >
                  <span className="ml-3">
                    <IconSupergoop />
                  </span>
                  <span className="ml-3 body-xs text-grey-2A">{supergoopText}</span>
                </button>
              </li>
            </ul>
            <div className=" hidden lg:block">{sunScreen}</div>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default HeaderNav;
